import React, { useEffect, useRef, useState } from 'react';
import { FaTrash, FaCloudUploadAlt } from 'react-icons/fa';
import curriculumImage from '../../images/Curriculum.png';
import './Antrag.css';
import axios from 'axios';
import { API_BASE_URL } from '../../constants';
import { toast } from 'react-toastify';
import { limitFileName } from '../../helpers/limitFileName';

interface FileUploadProps {
    id: string;
    name: string;
    label: string;
    description: string;
    accept: string;
    title: string;
    value: Array<{ name: string; guid: string }>;
    multiple?: boolean;
    onDelete: (fileName: string) => void;
    onUploadSuccess?: (fileName: string, guid: string) => void;
    antragId: number;
    tabindex?: number;
    className?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
    id,
    name,
    label,
    description,
    accept,
    title,
    value,
    multiple = false,
    onDelete,
    onUploadSuccess,
    antragId,
    tabindex,
    className
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [deletingFiles, setDeletingFiles] = useState<string[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        if (inputRef.current) {
            const dataTransfer = new DataTransfer();
            value.forEach(fileName => {
                const file = new File([""], fileName.name);
                dataTransfer.items.add(file);
            });
            inputRef.current.files = dataTransfer.files;
        }
    }, [value]);

    const handleDelete = async (fileName: string, guid: string) => {
        setDeletingFiles(prev => [...prev, fileName]);
        try {
            await axios.delete(`${API_BASE_URL}/application/document/${guid}`, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
            });
            onDelete(fileName);
            toast.success(`${fileName} erfolgreich gelöscht`);
        } catch (error) {
            console.error(`Error deleting file ${fileName}:`, error);
            toast.error(`Fehler beim Löschen von ${fileName}`);
        } finally {
            setDeletingFiles(prev => prev.filter(file => file !== fileName));
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !antragId) {
            if (!antragId) {
                toast.error('Bitte speichern Sie zuerst den Antrag');
            }
            return;
        }

        const files = Array.from(e.target.files);
        setIsUploading(true);

        for (const file of files) {
            try {
                const reader = new FileReader();

                reader.onload = async () => {
                    try {
                        const base64String = (reader.result as string).split(',')[1];
                        const uploadFileName = limitFileName(file.name);

                        const documentUploadItem = {
                            fileName: uploadFileName,
                            mimeType: file.type,
                            binaryData: base64String,
                            description: title
                        };

                        const url = `${API_BASE_URL}/application/document?antragId=${antragId}`;

                        const response = await axios.post(url, documentUploadItem, {
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${sessionStorage.getItem('token')}`
                            }
                        });


                        // Call the success callback IMMEDIATELY after successful upload
                        if (onUploadSuccess) {
                            // The response might be just a string (guid) or an object
                            const guid = typeof response.data === 'string'
                                ? response.data
                                : (response.data.guid || response.data);

                            onUploadSuccess(uploadFileName, guid);
                        }
                        setIsUploading(false);
                        //toast.success(`${file.name} erfolgreich hochgeladen`);
                    } catch (error) {
                        console.error(`Error uploading file ${file.name}:`, error);
                        toast.error(`Fehler beim Hochladen von ${file.name}`);
                        setIsUploading(false);
                    }
                };

                reader.onerror = (error) => {
                    console.error("Error reading file:", error);
                    toast.error(`Fehler beim Lesen von ${file.name}`);
                    setIsUploading(false);
                };

                reader.readAsDataURL(file);
            } catch (error) {
                console.error(`Error processing file ${file.name}:`, error);
                setIsUploading(false);
            }
        }

        // Reset input
        e.target.value = '';
    };

    const parseDescription = (description: string) => {
        const parts = description.split(/(<b>.*?<\/b>|<br \/>|<a href=".*?">.*?<\/a>)/g);
        return parts.map((part, index) => {
            if (part.startsWith('<b>') && part.endsWith('</b>')) {
                return <b key={index}>{part.slice(3, -4)}</b>;
            }
            if (part === '<br />') {
                return <br key={index} />;
            }
            if (part.startsWith('<a href="') && part.endsWith('</a>')) {
                const [, href, text] = part.match(/<a href="(.*?)">(.*?)<\/a>/) || [];
                return (
                    <a
                        tabIndex={-1}
                        key={index}
                        href={href === 'curriculum' ? curriculumImage : href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "blue", textDecoration: "underline" }}
                    >
                        {text}
                    </a>
                );
            }
            return part;
        });
    };

    return (
        <div className={`antrag-ausbildung${className ? ` ${className}` : ''}`}>
            {(label || description) ? (
                <div className="document-description">
                    <h5>{label}</h5>
                    <p style={{ maxWidth: "400px" }}>
                        {parseDescription(description).map((part, index) => (
                            <React.Fragment key={index}>
                                {part}
                            </React.Fragment>
                        ))}
                    </p>
                </div>
            ) : <div></div>}
            <div>
                <div className='form-group-ausbildung'>
                    <input
                        tabIndex={tabindex}
                        style={{ display: 'none' }}
                        id={id}
                        title={title}
                        type='file'
                        name={name}
                        accept={accept}
                        className='input-file'
                        onChange={handleFileUpload}
                        multiple={multiple}
                        ref={inputRef}
                    />
                    <label htmlFor={id} className='upload-btn'>
                        <FaCloudUploadAlt /> Datei hochladen
                    </label>
                </div>
            </div>
            <div>
                <div className='form-group-ausbildung'>
                    <div className='file-upload'>
                        {isUploading && (
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        )}
                        {!isUploading && value && value.map((doc, index) => (
                            <div key={index} className={`file ${deletingFiles.includes(doc.name) ? 'deleting' : ''}`}>
                                <span>{doc.name}</span>
                                <button
                                    type='button'
                                    className='delete-file'
                                    title='Entfernen Sie diese Datei'
                                    onClick={() => handleDelete(doc.name, doc.guid)}
                                    tabIndex={tabindex}
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FileUpload;